import type Badge from '$lib/components/atoms/badge/Badge.svelte';
import type { BadgeColor } from '$lib/components/atoms/badge/types';
import type { DotColor } from '$lib/components/map/markers/markerContent/types';
import type { ComponentProps } from 'svelte';
import type { PicturePoiCardScheme, PoiCardScheme, PoiData, RestStopPoiData } from '../types';
import type { PoiCardListScheme } from './types';
import defaultHotelImage from '$assets/pois/default-hotel.webp';
import defaultRestaurantImage from '$assets/pois/default-restaurant.webp';
import defaultTourismImage from '$assets/pois/default-tourism.webp';
import * as m from '$paraglide/messages';

export const cardScheme: Record<Exclude<PoiData['__typename'], 'PartnerPOI' | 'Partner2POI'>, PoiCardListScheme > = {
	AccommodationPOI: 'hotel',
	AccommodationMichelinPOI: 'hotelMichelin',
	RestaurantPOI: 'restaurant',
	RestaurantMichelinPOI: 'restaurantMichelin',
	ParkingPOI: 'parking',
	ServiceEVPOI: 'electric',
	TourismPOI: 'tourism',
	ServiceGasStationPOI: 'energy',
	ServiceRestStopPOI: 'rest',
	ServicePOI: 'electric',
};

export const fallBackSrc: Record<Exclude<PicturePoiCardScheme, 'energy'>, string> = {
	hotel: defaultHotelImage,
	hotelMichelin: defaultHotelImage,
	restaurant: defaultRestaurantImage,
	restaurantMichelin: defaultRestaurantImage,
	tourism: defaultTourismImage,
};

export const bgClass: Record<PicturePoiCardScheme, string> = {
	hotel: 'bg-neutral-weakest',
	hotelMichelin: 'bg-hotel-minimal',
	restaurant: 'bg-neutral-weakest',
	restaurantMichelin: 'bg-restaurant-minimal',
	tourism: 'bg-tourism-minimal',
};

export const colorClass: Record<PicturePoiCardScheme, string> = {
	hotel: 'text-hotel',
	hotelMichelin: 'text-hotel',
	restaurant: 'text-restaurant',
	restaurantMichelin: 'text-restaurant',
	tourism: 'text-tourism',
};

export const badgeIconName: Record<PoiCardListScheme, string> = {
	hotel: 'hotel',
	hotelMichelin: 'recommended',
	restaurant: 'restaurant',
	restaurantMichelin: 'recommended',
	tourism: 'recommended',
	energy: 'energy-pump',
	electric: 'flash',
	rest: 'rest-stop',
	parking: 'parking',
};

export const badgeColorClasses: Record<PicturePoiCardScheme, string> = {
	hotel: 'bg-hotel text-neutral-weakest',
	hotelMichelin: 'bg-hotel-michelin text-neutral-weakest border-hotel-michelin-strong border-1 border-solid',
	restaurant: 'bg-restaurant text-neutral-weakest',
	restaurantMichelin: 'bg-restaurant-michelin text-neutral-weakest border-restaurant-michelin-strong border-1 border-solid',
	tourism: 'bg-tourism text-neutral-weakest border-tourism-strong border-1 border-solid',
};

export const badgeText: Record<PoiCardListScheme, () => string | undefined> = {
	hotel: () => undefined,
	hotelMichelin: () => m.poi_card__michelin(),
	restaurant: () => undefined,
	restaurantMichelin: () => m.poi_card__michelin(),
	tourism: () => m.poi_card__michelin(),
	energy: () => undefined,
	electric: () => undefined,
	rest: () => undefined,
	parking: () => undefined,
};

export const bookIconName: Record<PoiCardListScheme, string | undefined> = {
	hotel: 'hotel',
	hotelMichelin: 'hotel',
	restaurant: 'restaurant',
	restaurantMichelin: 'restaurant',
	tourism: 'tourism',
	energy: undefined,
	electric: undefined,
	rest: undefined,
	parking: undefined,
};

export const infosClasses: Record<PoiCardScheme, string> = {
	hotel: 'text-hotel',
	hotelMichelin: 'text-hotel-michelin',
	restaurant: 'text-restaurant',
	restaurantMichelin: 'text-restaurant-michelin',
	tourism: 'text-tourism',
	parking: 'text-coloredneutral compactrows',
	energy: 'text-coloredneutral compactrows',
	electric: 'text-neutral-strongest compactrows',
	rest: 'text-coloredneutral compactrows',
	partner: 'text-coloredneutral compactrows',
};

export const defaultScoreBadgeProps: ComponentProps<typeof Badge> = {
	size: 'sm',
	color: 'car',
	isSquare: true,
	class: 'h-6 min-w-6 font-600 text-sm',
};

export const nonHRTBadgeColor: Record<Extract<PoiCardListScheme, 'energy' | 'parking' | 'electric' | 'rest'>, Extract<BadgeColor, 'restInverted' | 'electricInverted' | 'energyInverted' | 'parkingInverted'>> = {
	energy: 'energyInverted',
	parking: 'parkingInverted',
	electric: 'electricInverted',
	rest: 'restInverted',
};

export const mapPriceCategory: Record<string, DotColor> = {
	LOW: 'green',
	MEDIAN: 'orange',
	HIGH: 'red',
};

export const restStopVariantText: Record<Lowercase<NonNullable<RestStopPoiData['variant']>>, () => string > = {
	rest: () => m.poi_categories__rest_stop(),
	service: () => m.poi_datasheet__highlight__rest_stop__service(),
};

export const MAX_EAGER_CAROUSEL = 2;
